import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { ShippingCompanyFormData } from "@/types/shipping-company";

interface AddShippingCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const AddShippingCompanyDialog = ({ open, onOpenChange, onSuccess }: AddShippingCompanyDialogProps) => {
  const [formData, setFormData] = useState<ShippingCompanyFormData>({
    name_ar: "",
    name_en: "",
    code: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.from("shipping_companies").insert([formData]);

      if (error) throw error;

      toast.success("تم إضافة شركة النقل البحري بنجاح");
      setFormData({
        name_ar: "",
        name_en: "",
        code: "",
      });
      onSuccess();
    } catch (error) {
      console.error("Error adding shipping company:", error);
      toast.error("حدث خطأ أثناء إضافة شركة النقل البحري");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" dir="rtl">
        <DialogHeader>
          <DialogTitle>إضافة شركة نقل بحري جديدة</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name_ar">الاسم بالعربية *</Label>
            <Input
              id="name_ar"
              value={formData.name_ar}
              onChange={(e) => setFormData(prev => ({ ...prev, name_ar: e.target.value }))}
              placeholder="أدخل اسم الشركة بالعربية"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name_en">الاسم بالإنجليزية *</Label>
            <Input
              id="name_en"
              value={formData.name_en}
              onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
              placeholder="أدخل اسم الشركة بالإنجليزية"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="code">كود الشركة *</Label>
            <Input
              id="code"
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
              placeholder="أدخل كود الشركة (مثل: MSC)"
              required
              maxLength={10}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              إلغاء
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "جاري الإضافة..." : "إضافة الشركة"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};